import { useState, useEffect } from 'react';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';

async function fetchPublic(endpoint) {
  const url = `${API_BASE}/public${endpoint}?_t=${Date.now()}`;
  const res = await fetch(url, { cache: 'no-store' });
  if (!res.ok) throw new Error(`API error: ${res.status}`);
  return res.json();
}

async function fetchWithRetry(endpoint) {
  try {
    return await fetchPublic(endpoint);
  } catch (err) {
    await new Promise(r => setTimeout(r, 1500));
    return fetchPublic(endpoint);
  }
}

export function useProjects() {
  const [projects, setProjects] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchWithRetry('/projects')
      .then(setProjects)
      .catch(() => {
        import('../data/projects').then(m => setProjects(m.projects));
      })
      .finally(() => setLoading(false));
  }, []);

  return { projects, loading };
}

export function useProject(slug) {
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!slug) return;
    fetchWithRetry(`/projects/${slug}`)
      .then(setProject)
      .catch(() => {
        import('../data/projects').then(m => {
          setProject(m.projects.find(p => p.slug === slug || p.id === slug) || null);
        });
      })
      .finally(() => setLoading(false));
  }, [slug]);

  return { project, loading };
}

export function useServices() {
  const [services, setServices] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // 1. Try consolidated site-data.json (services + settings, regenerated on every admin save)
    // 2. Fall back to legacy nav-data.json (transitional — pre-deployment backward compat)
    // 3. Fall back to live API
    fetch(`/site-data.json?_t=${Date.now()}`, { cache: 'no-store' })
      .then(res => { if (!res.ok) throw new Error(); return res.json(); })
      .then(data => { if (!data?.services) throw new Error(); return data.services; })
      .catch(() =>
        fetch(`/nav-data.json?_t=${Date.now()}`, { cache: 'no-store' })
          .then(res => { if (!res.ok) throw new Error(); return res.json(); })
          .catch(() => fetchPublic('/services'))
      )
      .then(setServices)
      .catch(() => setServices(null))
      .finally(() => setLoading(false));
  }, []);

  return { services, loading };
}

export function useService(slug) {
  const [service, setService] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!slug) return;
    fetchWithRetry(`/services/${slug}`)
      .then(setService)
      .catch(() => {
        import('../data/services').then(m => {
          setService(m.serviceCategories.find(s => s.slug === slug || s.id === slug) || null);
        });
      })
      .finally(() => setLoading(false));
  }, [slug]);

  return { service, loading };
}

export function useJobs() {
  const [jobs, setJobs] = useState(null);
  const [companyBenefits, setCompanyBenefits] = useState(null);
  const [teamValues, setTeamValues] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      fetchWithRetry('/jobs'),
      fetchWithRetry('/company-benefits'),
      fetchWithRetry('/team-values'),
    ])
      .then(([j, cb, tv]) => {
        setJobs(j);
        setCompanyBenefits(cb);
        setTeamValues(tv);
      })
      .catch(() => {
        import('../data/jobs').then(m => {
          setJobs(m.jobs);
          setCompanyBenefits(m.companyBenefits);
          setTeamValues(m.teamValues);
        });
      })
      .finally(() => setLoading(false));
  }, []);

  return { jobs, companyBenefits, teamValues, loading };
}

export function usePageContent(pageKey) {
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!pageKey) return;
    fetchWithRetry(`/pages/${pageKey}`)
      .then(setContent)
      .catch(() => setContent(null))
      .finally(() => setLoading(false));
  }, [pageKey]);

  return { content, loading };
}

export function useLegal(slug) {
  const [legal, setLegal] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!slug) return;
    fetchPublic(`/legal/${slug}`)
      .then((data) => setLegal(data.content))
      .catch(() => {
        import('../data/legal').then(m => {
          setLegal(m[slug] || null);
        });
      })
      .finally(() => setLoading(false));
  }, [slug]);

  return { legal, loading };
}

export function useSettings() {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Try static site-data.json first (cache-busted), fall back to live API
    fetch(`/site-data.json?_t=${Date.now()}`, { cache: 'no-store' })
      .then(res => { if (!res.ok) throw new Error(); return res.json(); })
      .then(data => { if (!data?.settings) throw new Error(); return data.settings; })
      .catch(() => fetchPublic('/settings'))
      .then(setSettings)
      .catch(() => setSettings(null))
      .finally(() => setLoading(false));
  }, []);

  return { settings, loading };
}

export function submitInquiry(data) {
  return fetch(`${API_BASE}/public/inquiries`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  }).then(res => {
    if (!res.ok) throw new Error('Anfrage fehlgeschlagen');
    return res.json();
  });
}
